import { IconCheckmark2 } from 'central-icons/IconCheckmark2';
import classNames from 'classnames';
import { type ReactNode, useEffect, useRef } from 'react';

import {
  Dialog,
  RailModalBody,
  RailModalCloseButton,
  RailModalContent,
  RailModalDivider,
  RailModalFooter,
  RailModalFooterSpacer,
  RailModalHeader,
  RailModalPane,
  RailModalRail,
  RailModalRailItem,
  Button as UiButton,
} from '@revelio/ui';

import type {
  TutorialVideoGroup,
  VideoData,
} from '../../shared/ui/tutorial/Tutorial';
import styles from './tutorial-modal.module.css';

interface TutorialModalContentProps {
  isOpen: boolean;
  onClose: () => void;
  onCloseComplete?: () => void;
  closeOnBackdropClick?: boolean;
  groups: TutorialVideoGroup[];
  activeVideo: VideoData | null;
  watchedIds: ReadonlySet<string>;
  onSelectVideo: (videoId: string) => void;
  /** Primary footer call to action, or null when there is nothing worth
   *  offering. The container owns that decision: it knows the current route
   *  (no point offering the page you are already on) and the viewer's
   *  entitlements (offer Contact Sales, not a route they cannot open). */
  primaryAction?: { label: string; onClick: () => void } | null;
  player: ReactNode;
  /** A note appended to one video's description. Asked of every video, not
   *  only the active one, since each hidden description holds its own height. */
  descriptionSuffixFor?: (video: VideoData) => ReactNode;
}

export const TutorialModalContent = ({
  isOpen,
  onClose,
  onCloseComplete,
  closeOnBackdropClick,
  groups,
  activeVideo,
  watchedIds,
  onSelectVideo,
  primaryAction,
  player,
  descriptionSuffixFor,
}: TutorialModalContentProps) => {
  // The dialog has no exit animation, so "close complete" is simply the
  // open -> closed transition. Tracked rather than folded into `onClose`
  // because the container also closes the tutorial by flipping `isOpen`, and
  // it marks the tour complete from here.
  const wasOpen = useRef(isOpen);
  useEffect(() => {
    if (wasOpen.current && !isOpen) {
      onCloseComplete?.();
    }
    wasOpen.current = isOpen;
  }, [isOpen, onCloseComplete]);

  const flatVideos = groups.flatMap((group) => group.videos);
  const activeIndex = flatVideos.findIndex(
    (video) => video.id === activeVideo?.id
  );
  const previousVideo = activeIndex > 0 ? flatVideos[activeIndex - 1] : null;
  const nextVideo =
    activeIndex >= 0 && activeIndex < flatVideos.length - 1
      ? flatVideos[activeIndex + 1]
      : null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      size="xl"
      closeOnBackdropClick={closeOnBackdropClick}
      closeButton={false}
      className={styles.content}
      bodyClassName={styles.dialogBody}
      aria-label="Tutorials"
    >
      <RailModalContent>
        <RailModalHeader>Tutorials</RailModalHeader>
        <RailModalCloseButton
          onClick={onClose}
          data-testid="tutorial-modal-close-btn"
        />
        <RailModalDivider />
        <RailModalBody>
          <RailModalRail aria-label="Tutorial sections">
            {flatVideos.map((video) => (
              <RailModalRailItem
                key={video.id}
                isActive={video.id === activeVideo?.id}
                trailing={
                  watchedIds.has(video.id) && <IconCheckmark2 size={16} />
                }
                onClick={() => onSelectVideo(video.id)}
              >
                {video.name}
              </RailModalRailItem>
            ))}
          </RailModalRail>
          <RailModalPane>
            <div
              className={styles.playerFrame}
              data-testid="tutorial-video-player"
            >
              {player}
            </div>
            <div className={styles.paneText}>
              <p className={styles.paneTitle}>{activeVideo?.name}</p>
              {/* Every description shares one grid cell with only the active
                  one visible, so the pane holds the tallest one's height and
                  the modal does not jump between videos. */}
              <div className={styles.descriptionStack}>
                {flatVideos.map((video) => (
                  <p
                    key={video.id}
                    className={classNames(
                      styles.paneDescription,
                      video.id !== activeVideo?.id && styles.descriptionHidden
                    )}
                  >
                    {video.description}
                    {descriptionSuffixFor?.(video)}
                  </p>
                ))}
              </div>
            </div>
          </RailModalPane>
        </RailModalBody>
        <RailModalDivider />
        <RailModalFooter>
          {previousVideo && (
            <UiButton
              variant="ghost"
              onClick={() => onSelectVideo(previousVideo.id)}
            >
              Back
            </UiButton>
          )}
          {nextVideo && (
            <UiButton
              variant="ghost"
              onClick={() => onSelectVideo(nextVideo.id)}
            >
              Next
            </UiButton>
          )}
          <RailModalFooterSpacer />
          {primaryAction && (
            <UiButton color="primary" onClick={primaryAction.onClick}>
              {primaryAction.label}
            </UiButton>
          )}
        </RailModalFooter>
      </RailModalContent>
    </Dialog>
  );
};
